import type { JSONContent } from '@tiptap/core';
import { mergeRuns, type RichRun, type RichText } from './coach-guide';

// Converts between the guide's stored rich text (runs) and the Tiptap editor's
// document. The editor schema is limited to exactly what runs can hold —
// paragraphs, hard breaks, bold, italic, link, font size — so anything pasted
// in is reduced to that and always round-trips.

type Mark = NonNullable<JSONContent['marks']>[number];

function marksFor(run: RichRun): Mark[] {
  const marks: Mark[] = [];
  if (run.bold) marks.push({ type: 'bold' });
  if (run.italic) marks.push({ type: 'italic' });
  if (run.href) marks.push({ type: 'link', attrs: { href: run.href } });
  if (run.size) marks.push({ type: 'textStyle', attrs: { fontSize: `${run.size}px` } });
  return marks;
}

/** Stored runs → editor document. Each "\n" starts a new paragraph. */
export function runsToDoc(runs: RichText): JSONContent {
  const paragraphs: JSONContent[] = [{ type: 'paragraph', content: [] }];
  for (const run of runs) {
    run.text.split('\n').forEach((segment, i) => {
      if (i > 0) paragraphs.push({ type: 'paragraph', content: [] });
      if (!segment) return;
      const marks = marksFor(run);
      paragraphs[paragraphs.length - 1].content!.push({ type: 'text', text: segment, ...(marks.length ? { marks } : {}) });
    });
  }
  return { type: 'doc', content: paragraphs.map((p) => (p.content!.length ? p : { type: 'paragraph' })) };
}

function runFor(node: JSONContent): RichRun {
  const run: RichRun = { text: node.text ?? '' };
  for (const mark of node.marks ?? []) {
    if (mark.type === 'bold') run.bold = true;
    else if (mark.type === 'italic') run.italic = true;
    else if (mark.type === 'link' && typeof mark.attrs?.href === 'string') run.href = mark.attrs.href;
    else if (mark.type === 'textStyle' && mark.attrs?.fontSize) {
      const px = parseFloat(String(mark.attrs.fontSize));
      if (Number.isFinite(px)) run.size = px;
    }
  }
  return run;
}

/** Editor document → stored runs. Paragraphs and hard breaks become "\n". */
export function docToRuns(doc: JSONContent): RichText {
  const runs: RichRun[] = [];
  (doc.content ?? []).forEach((block, i) => {
    if (i > 0) runs.push({ text: '\n' });
    for (const node of block.content ?? []) {
      if (node.type === 'text') runs.push(runFor(node));
      else if (node.type === 'hardBreak') runs.push({ text: '\n' });
    }
  });
  return mergeRuns(runs);
}
